import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { contentAPI, taskAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { Link2, Check, X, ExternalLink, Clock, CheckCircle, XCircle } from 'lucide-react';
import './TrackPost.css';

function TrackPost() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    url: '',
    platform: 'facebook',
    caption: '',
    campaign_id: '',
    task_id: ''
  });
  const [showSuccess, setShowSuccess] = useState(false);

  // Fetch user's submissions
  const { data: submissionsData, isLoading } = useQuery({
    queryKey: ['content-submissions'],
    queryFn: () => contentAPI.getSubmissions().then(res => res.data),
  });

  // Fetch tasks for dropdown
  const { data: tasksData } = useQuery({
    queryKey: ['tasks'],
    queryFn: () => taskAPI.getAll().then(res => res.data),
  });

  // Submit mutation
  const submitMutation = useMutation({
    mutationFn: (data) => contentAPI.trackPost(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['content-submissions']);
      setFormData({
        url: '',
        platform: 'facebook',
        caption: '',
        campaign_id: '',
        task_id: ''
      });
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    },
  });

  const submissions = submissionsData?.submissions || [];
  const tasks = tasksData?.tasks || [];

  const handleSubmit = (e) => {
    e.preventDefault();
    submitMutation.mutate(formData);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle size={16} className="status-icon approved" />;
      case 'rejected':
        return <XCircle size={16} className="status-icon rejected" />;
      default:
        return <Clock size={16} className="status-icon pending" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return '#10b981';
      case 'rejected': return '#ef4444';
      default: return '#f59e0b';
    }
  };

  return (
    <div className="track-post-page">
      <div className="page-header">
        <h1><Link2 size={28} /> Track Your Posts</h1>
        <p>Submit links to your social media posts for verification and tracking</p>
      </div>

      {/* Submit Form */}
      <div className="submit-section">
        <h2>Submit a Post</h2>
        <p className="helper-text">
          Since Facebook deprecated personal timeline posting via API, submit your post URL here for manual verification.
        </p>

        <form onSubmit={handleSubmit} className="submit-form">
          <div className="form-group">
            <label>Social Platform *</label>
            <select
              value={formData.platform}
              onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
              required
            >
              <option value="facebook">Facebook</option>
              <option value="instagram">Instagram</option>
              <option value="twitter">Twitter</option>
              <option value="linkedin">LinkedIn</option>
              <option value="tiktok">TikTok</option>
              <option value="youtube">YouTube</option>
            </select>
          </div>

          <div className="form-group">
            <label>Post URL *</label>
            <input
              type="url"
              value={formData.url}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              placeholder="https://facebook.com/yourprofile/posts/123456..."
              required
            />
            <small>Paste the full URL of your post</small>
          </div>

          <div className="form-group">
            <label>Caption/Description</label>
            <textarea
              value={formData.caption}
              onChange={(e) => setFormData({ ...formData, caption: e.target.value })}
              placeholder="What did you post about?"
              rows={3}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Related Task (Optional)</label>
              <select
                value={formData.task_id}
                onChange={(e) => setFormData({ ...formData, task_id: e.target.value })}
              >
                <option value="">No related task</option>
                {tasks.map(task => (
                  <option key={task.id} value={task.id}>
                    {task.title}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <button 
            type="submit" 
            className="btn-submit"
            disabled={submitMutation.isPending}
          >
            {submitMutation.isPending ? 'Submitting...' : 'Submit Post'}
          </button>

          {showSuccess && (
            <div className="success-message">
              <Check size={16} /> Post submitted successfully! Pending admin verification.
            </div>
          )}

          {submitMutation.isError && (
            <div className="error-message">
              <X size={16} /> {submitMutation.error?.response?.data?.error || 'Failed to submit post'}
            </div>
          )}
        </form>
      </div>

      {/* Submissions History */}
      <div className="submissions-section">
        <h2>Your Submissions</h2>

        {isLoading ? (
          <div className="loading">Loading submissions...</div>
        ) : submissions.length === 0 ? (
          <div className="empty-state">
            <Link2 size={48} />
            <p>No submissions yet</p>
            <small>Submit your first post above to get started</small>
          </div>
        ) : (
          <div className="submissions-list">
            {submissions.map(submission => (
              <div key={submission.id} className="submission-card">
                <div className="submission-header">
                  <div className="submission-title">
                    <h3>{submission.title}</h3>
                    <span 
                      className="status-badge"
                      style={{ 
                        backgroundColor: getStatusColor(submission.status),
                        color: 'white'
                      }}
                    >
                      {getStatusIcon(submission.status)}
                      {submission.status}
                    </span>
                  </div>
                  <a 
                    href={submission.media_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="view-post-btn"
                  >
                    <ExternalLink size={16} /> View Post
                  </a>
                </div>

                {submission.caption && (
                  <p className="submission-caption">{submission.caption}</p>
                )}

                <div className="submission-meta">
                  <span>
                    Submitted: {new Date(submission.created_at).toLocaleDateString()}
                  </span>
                  {submission.reviewed_at && (
                    <span>
                      Reviewed: {new Date(submission.reviewed_at).toLocaleDateString()}
                    </span>
                  )}
                </div>

                {submission.feedback && (
                  <div className="submission-feedback">
                    <strong>Admin Feedback:</strong> {submission.feedback}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default TrackPost;
